import type { ReactElement } from 'react'

import { cn } from '../lib/cn'
import { selectedChoice } from '../lib/surfaces'

export interface SegmentedOption<TValue extends string> {
  readonly value: TValue
  readonly label: string
  /** A refusal — «Non ci sono» — is drawn red once chosen, the way every other reader of it sees it. */
  readonly tone?: 'danger'
}

/** The chosen refusal: the danger fill, with the same edge and weight as `selectedChoice`. */
const selectedRefusal = 'border-foreground bg-danger font-semibold text-danger-foreground'

function chosenClass<TValue extends string>(option: SegmentedOption<TValue>): string {
  return option.tone === 'danger' ? selectedRefusal : selectedChoice
}

export interface SegmentedControlProps<TValue extends string> {
  readonly label: string
  readonly options: readonly SegmentedOption<TValue>[]
  /** `null` while nothing is chosen yet: no button is pressed. */
  readonly value: TValue | null
  readonly onChange: (next: TValue) => void
  readonly className?: string
  /** In flight: the buttons refuse the pointer and say so, rather than looking live and
   *  swallowing the click. `Combobox` takes the same prop for the same reason. */
  readonly disabled?: boolean
  /** The label names the group for a screen reader and is not written on the page: where the thing
   *  the buttons are about — a player's name — stands right beside them, and a second copy of it
   *  would be read twice. */
  readonly hideLabel?: boolean
  /** Wiring for an error owned by the caller — the same two attributes
   *  `FormFieldControlProps` hands a control, applied here to the group. */
  readonly 'aria-describedby'?: string
  readonly 'aria-invalid'?: boolean
}

/** A few short values as a row of buttons instead of a dropdown: three of them fit one line
 *  and are thumb-sized, where a `Select` would hide them behind a tap. */
export function SegmentedControl<TValue extends string>({
  label,
  options,
  value,
  onChange,
  className,
  disabled = false,
  hideLabel = false,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
}: SegmentedControlProps<TValue>): ReactElement {
  return (
    <div className={cn('grid gap-1.5', className)}>
      {hideLabel ? null : <span className="text-sm leading-none font-medium">{label}</span>}
      <div
        className="flex gap-1 rounded-pill bg-muted p-1"
        role="group"
        aria-label={label}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
      >
        {options.map((option) => (
          <button
            key={option.value}
            aria-pressed={option.value === value}
            className={cn(
              // Each takes the room its words need and shares out the rest: `flex-1` cut the row into
              // equal thirds, and «Non convocato» broke in two on a phone.
              'flex-auto rounded-pill border px-2 py-2 text-note whitespace-nowrap transition-colors',
              'focus-visible:focus-volee focus-visible:outline-none',
              'disabled:cursor-not-allowed disabled:opacity-50',
              option.value === value ? chosenClass(option) : 'border-transparent text-foreground',
            )}
            disabled={disabled}
            type="button"
            onClick={() => {
              onChange(option.value)
            }}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  )
}
